import { useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages } from 'react-intl.macro';
import { FormattedMessage } from 'react-intl';

import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  type ChartConfiguration,
} from 'chart.js';

import './ChartDoc.scss';

import { DangerAlert, WaitingAlert } from '@tmapy/style-guide';
import { useMessage } from '@tmapy/intl';

import { QUERY_STATE } from '../constants';
import { useGraphDataResource } from './useChartDataResource';

const GRAPH_COLOR = 'rgba(2, 118, 177, 0.7)';

export const msg = defineMessages<any>({
  dmvsSourceTitle: {
    id: 'chart.dmvsSourceTitle',
    defaultMessage: 'Počty žádostí o data dle zdroje výdeje',
  },
  queryStateWaiting: {
    id: 'chart.dmvsSource.queryStateWaiting',
    defaultMessage: 'Načítám statistická data dle zdroje výdeje - Čekejte prosím...',
  },
  queryStateFail: {
    id: 'chart.dmvsSource.queryStateFail',
    defaultMessage: 'Načtení statistických dat dle zdroje výdeje selhalo.',
  },
  chartDatasetLabel: {
    id: 'chart.dmvsSource.chartDatasetLabel',
    defaultMessage: 'Počet žádostí o data',
  },
});

export const DmvsSourceChart: React.FC = () => {
  const formatMessage = useMessage();
  const dmvsSourceGraphRef = useRef<HTMLCanvasElement | null>(null);
  const [dmvsSourceChart, setDmvsSourceChart] = useState<Chart | null>(null);
  const [queryState, setQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const dmvsSourceResource = useGraphDataResource({ graph_type: 'dmvs_source_order_count' });
  const dmvsSourceData = useMemo(() => {
    setQueryState(dmvsSourceResource.queryState);
    if (dmvsSourceResource.queryState !== QUERY_STATE.SUCCESS) return;

    return dmvsSourceResource.data?.results;
  }, [dmvsSourceResource.data]);

  useEffect(() => {
    if (!dmvsSourceGraphRef.current || dmvsSourceChart || !dmvsSourceData?.length) return;

    Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip);

    setDmvsSourceChart(
      new Chart(dmvsSourceGraphRef.current, {
        data: {
          labels: dmvsSourceData.map((item: Record<string, string | number>) => item.dmvs_source),
          datasets: [
            {
              type: 'bar',
              label: formatMessage(msg.chartDatasetLabel),
              data: dmvsSourceData.map((item: Record<string, string | number>) => item.order_count),
              backgroundColor: GRAPH_COLOR,
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: false,
            },
            legend: {
              display: false,
            },
          },
        },
      } as ChartConfiguration),
    );
  }, [dmvsSourceGraphRef.current, dmvsSourceChart, dmvsSourceData]);

  // cleanup
  useEffect(() => {
    return () => {
      Chart.unregister(BarController, BarElement, LinearScale, CategoryScale, Tooltip);
    };
  }, []);

  return (
    <>
      {queryState === QUERY_STATE.WAITING && (
        <WaitingAlert>
          <FormattedMessage {...msg.queryStateWaiting} />
        </WaitingAlert>
      )}
      {queryState === QUERY_STATE.FAIL && (
        <DangerAlert>
          <FormattedMessage {...msg.queryStateFail} />
        </DangerAlert>
      )}

      <div
        className='dmx-chart--rightCol sg-u-vs-4'
        style={{ display: `${queryState === QUERY_STATE.SUCCESS ? 'block' : 'none'}` }}
      >
        <section>
          <h3 className='sg-u-type-h2 sg-a-ml-2'>
            <FormattedMessage {...msg.dmvsSourceTitle} />
          </h3>
          <div className='sg-u-box sg-a-mt-1 dmx-a-mw-fc'>
            <canvas ref={dmvsSourceGraphRef} className='dmx-chart-canvas' />
          </div>
        </section>
      </div>
    </>
  );
};
